'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import H1 from 'UIComponents/elements/headings/H1';
import UIListingPage from 'UIComponents/page/UIListingPage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { GYPSUM, OLAF } from 'HubStyleTokens/colors';
import ReviewsHeader from 'marketplace-ui-reviews/components/ReviewsHeader';
import ReviewsFilterSortDropdowns from 'marketplace-ui-reviews/components/ReviewsFilterSortDropdowns';
import ReviewsPaginatedList from 'marketplace-ui-reviews/components/ReviewsPaginatedList';
import ReviewsListStats from 'marketplace-ui-reviews/components/ReviewsListStats';
import { REVIEWS_PER_PAGE, SEARCH_KEY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import AllReviewsReviewRenderer from '../../components/detail/reviews/AllReviewsReviewRenderer';
import ReviewWizard from '../../components/detail/reviews/ReviewWizard';
import WriteReviewButton from '../../components/detail/reviews/WriteReviewButton';
import ReviewsListEmptyState from '../../components/detail/reviews/ReviewsListEmptyState';
import { DetailMainSection as MainSection } from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import Header from '../../components/detail/Header';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import { isUninitialized } from 'ecosystem-marketplace-core/utils/requestState';
import { getUsageTracker } from '../../utils/usageTracker';
import { getIsPrerenderUserAgent } from 'ecosystem-marketplace-core/utils/userAgent';
import { makeGetListingView, makeGetReviewabilityFetchStatus } from '../../selectors/details';
import { getReviewFilters, getReviews, getReviewStats, getReviewsTotal } from '../../selectors/reviews';
import { getReviewsFetchRequestState, getReviewStatsRequestState } from '../../selectors/requestStates';
import { PublicOnly, getIsCustomer } from '../../selectors/auth';
import { isSucceeded } from 'ecosystem-marketplace-core/utils/requestState';
import { getReviews as fetchReviews, getReviewsStats as fetchReviewStats, resetReviewsFetch, resetReviewsStatsFetch, updateReviewFilters, updateReviewability } from '../../actions/ReviewsActions';
import { useParams } from 'react-router-dom';
import { MINIMUM_REVIEWS_NEEDED_TO_DISPLAY } from '../../constants/reviews';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
const REVIEWS_WIDTH = 900;

const EmptyLoadingComponent = () => /*#__PURE__*/_jsx("div", {});

const AllReviewsPage = () => {
  const {
    superCategory,
    category,
    slug
  } = useParams();
  const dispatch = useDispatch();
  const getListingView = useMemo(makeGetListingView, []);
  const getReviewabilityFetchStatus = useMemo(makeGetReviewabilityFetchStatus, []);
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const isCustomer = useSelector(getIsCustomer);
  const isPrerenderUserAgent = getIsPrerenderUserAgent();
  const preferredLanguage = getLang();
  const listingView = useSelector(state => getListingView(state, {
    slug
  }));
  const reviews = useSelector(getReviews);
  const reviewsTotal = useSelector(getReviewsTotal);
  const reviewsFetchStatus = useSelector(getReviewsFetchRequestState);
  const reviewStats = useSelector(getReviewStats);
  const reviewStatsFetchStatus = useSelector(getReviewStatsRequestState);
  const reviewabilityStatus = useSelector(state => getReviewabilityFetchStatus(state, {
    slug
  }));
  const [wizardOpen, setWizardOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageViewTracked, setPageViewTracked] = useState(false);
  const tracker = getUsageTracker();
  const reviewsCount = reviewStats.get('count');
  const {
    name,
    iconUrl,
    id,
    offeringId,
    offering: {
      certifiedAt,
      homemade
    },
    tagline,
    reviewability
  } = listingView;
  const reviewFilters = useSelector(getReviewFilters).set('entityId', offeringId);
  useEffect(() => {
    if (isUninitialized(reviewsFetchStatus)) {
      dispatch(fetchReviews(reviewFilters.toSearchQuery()));
    }
  }, [dispatch, reviewsFetchStatus, reviewFilters]);
  useEffect(() => {
    if (isUninitialized(reviewStatsFetchStatus)) {
      dispatch(fetchReviewStats(offeringId));
    }
  }, [dispatch, reviewStatsFetchStatus, offeringId]);
  useEffect(() => {
    return () => {
      dispatch(resetReviewsFetch());
      dispatch(resetReviewsStatsFetch());
    };
  }, [dispatch]);
  const eventProperties = useMemo(() => Object.assign({}, listingView.getEventProperties(), {
    slug,
    screen: 'all reviews',
    preferredLanguage
  }), [listingView, preferredLanguage, slug]);
  const trackInteraction = useCallback((action, section, value, properties = {}) => {
    tracker.track('listing-detail-interaction', Object.assign({
      action,
      section,
      value
    }, properties, {}, eventProperties));
  }, [tracker, eventProperties]);
  const onUpdateFilters = useCallback(({
    textKey,
    value
  }) => {
    const trackUpdate = () => trackInteraction(`change reviews ${textKey}`, 'review', `${value}`);

    if (textKey === SEARCH_KEY) {
      setSearchTerm(value);
    }

    dispatch(updateReviewFilters({
      [textKey]: value
    }, trackUpdate));
  }, [setSearchTerm, trackInteraction, dispatch]);
  const documentTitle = useMemo(() => {
    return unescapedText('appMarketplace.detail.reviews.all.htmlTitle', {
      name
    });
  }, [name]);
  useEffect(() => {
    document.title = documentTitle;

    if (!pageViewTracked) {
      tracker.track('all-reviews-pageview', Object.assign({}, eventProperties));
      setPageViewTracked(true);
    }
  }, [documentTitle, eventProperties, pageViewTracked, tracker]);
  const renderedHeader = useMemo(() => {
    const breadcrumbs = /*#__PURE__*/_jsx(UIBreadcrumbs, {
      className: "m-bottom-1 m-top-0 p-left-0",
      children: /*#__PURE__*/_jsx(UIRouterLink, {
        to: `/${superCategory}/${category}/${slug}#reviews`,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.reviews.all.back",
          options: {
            name
          }
        })
      })
    });

    return /*#__PURE__*/_jsx(MainSectionInner, {
      children: /*#__PURE__*/_jsx(Header, {
        appId: id,
        appName: name,
        breadcrumbs: breadcrumbs,
        certifiedAt: certifiedAt,
        headingLevel: reviewsCount ? 'h2' : 'h1',
        homemade: homemade,
        iconUrl: iconUrl,
        isCustomer: isCustomer,
        showTabs: false,
        showConnectButton: false,
        showSetUpUrl: false,
        tagline: tagline,
        wrapperStyle: {
          paddingBottom: '24px'
        }
      })
    });
  }, [certifiedAt, name, homemade, iconUrl, id, isCustomer, reviewsCount, tagline, superCategory, category, slug]);
  const {
    inline,
    overallDistClassName
  } = useResponsiveProps({
    inline: {
      xs: false,
      md: true
    },
    overallDistClassName: {
      xs: undefined,
      md: 'm-all-auto'
    }
  });
  const renderedReviewsListStats = useMemo(() => {
    if (reviewsCount && offeringId) {
      return /*#__PURE__*/_jsxs("div", {
        style: {
          maxWidth: REVIEWS_WIDTH,
          width: '100%'
        },
        children: [/*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          justify: "between",
          direction: isMobileView ? 'column' : 'row',
          children: [/*#__PURE__*/_jsx(H1, {
            "aria-level": "1",
            className: "m-top-10 m-bottom-8 is--heading-2",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.reviews.all.headingText",
              options: {
                name
              }
            })
          }), /*#__PURE__*/_jsx(WriteReviewButton, {
            className: !isMobileView && 'm-left-4',
            offeringId: offeringId,
            onClick: () => setWizardOpen(true),
            reviewability: reviewability,
            size: "md"
          })]
        }), reviewsCount >= MINIMUM_REVIEWS_NEEDED_TO_DISPLAY && /*#__PURE__*/_jsx(ReviewsListStats, {
          className: "m-bottom-15",
          inline: inline,
          onUpdateFilters: onUpdateFilters,
          overallDistClassName: overallDistClassName,
          reviewStats: reviewStats,
          reviewType: ReviewTypes.APP,
          width: 300
        })]
      });
    } else {
      return /*#__PURE__*/_jsx("div", {});
    }
  }, [reviewsCount, offeringId, isMobileView, name, reviewability, inline, onUpdateFilters, overallDistClassName, reviewStats]);
  const renderedReviewsListBody = useMemo(() => {
    if (reviewsCount) {
      return /*#__PURE__*/_jsxs("div", {
        style: {
          maxWidth: REVIEWS_WIDTH,
          width: '100%'
        },
        children: [/*#__PURE__*/_jsx(ReviewsHeader, {
          onUpdateFilters: onUpdateFilters,
          reviewsCount: reviewsTotal,
          reviewFilters: reviewFilters
        }), /*#__PURE__*/_jsx(ReviewsFilterSortDropdowns, {
          onUpdateFilters: onUpdateFilters,
          reviewFilters: reviewFilters,
          reviewStats: reviewStats
        }), /*#__PURE__*/_jsx(ReviewsPaginatedList, {
          onUpdateFilters: onUpdateFilters,
          maxWidth: REVIEWS_WIDTH,
          ReviewRenderer: AllReviewsReviewRenderer,
          reviews: reviews,
          reviewFilters: reviewFilters,
          reviewsFetchStatus: reviewsFetchStatus,
          reviewsTotal: reviewsTotal,
          searchTerm: searchTerm,
          showPagination: !isPrerenderUserAgent && reviewsTotal >= REVIEWS_PER_PAGE
        })]
      });
    } else {
      return /*#__PURE__*/_jsx("div", {
        className: "m-top-15",
        children: /*#__PURE__*/_jsx(ReviewsListEmptyState, {
          offeringId: offeringId,
          reviewability: reviewability,
          onWriteReviewClick: () => setWizardOpen(true)
        })
      });
    }
  }, [isPrerenderUserAgent, reviewsCount, offeringId, onUpdateFilters, reviews, reviewStats, reviewsTotal, reviewFilters, reviewsFetchStatus, reviewability, searchTerm]);
  return /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewabilityStatus,
    children: /*#__PURE__*/_jsxs(NavMarker, {
      name: "DETAIL_REVIEWS_LOAD",
      children: [/*#__PURE__*/_jsx(PublicOnly, {
        children: /*#__PURE__*/_jsx(DocumentHead, {
          children: /*#__PURE__*/_jsx(PrerenderReady, {
            ready: isSucceeded(reviewsFetchStatus)
          })
        })
      }), /*#__PURE__*/_jsxs(UIListingPage, {
        contentAreaFlush: true,
        headerComponent: renderedHeader,
        mainSectionBackgroundColor: OLAF,
        pageBackgroundColor: GYPSUM,
        children: [/*#__PURE__*/_jsx(MainSection, {
          children: /*#__PURE__*/_jsxs(UIFlex, {
            direction: "column",
            align: "center",
            alignSelf: "center",
            className: "p-bottom-15",
            children: [/*#__PURE__*/_jsx(LoadingWrapper, {
              LoadingComponent: EmptyLoadingComponent,
              requestStatus: reviewStatsFetchStatus,
              children: renderedReviewsListStats
            }), renderedReviewsListBody]
          })
        }), wizardOpen && /*#__PURE__*/_jsx(ReviewWizard, {
          appName: name,
          appIcon: iconUrl,
          offeringId: offeringId,
          onClose: () => setWizardOpen(false),
          onSuccess: () => {
            setWizardOpen(false);
            dispatch(updateReviewability(slug));
          },
          onTrackInteraction: trackInteraction,
          reviewability: reviewability
        })]
      })]
    })
  });
};

export default AllReviewsPage;